import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { PaymentForm, CreditCard } from "react-square-web-payments-sdk";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Lightning, ShieldCheck, Lock } from "@phosphor-icons/react";

export default function Checkout() {
  const [params] = useSearchParams();
  const plan = params.get("plan") || "intermediate";
  const [tiers, setTiers] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(null);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoErr, setPromoErr] = useState("");
  const { user, refresh } = useAuth();
  const navigate = useNavigate();

  const APP_ID = process.env.REACT_APP_SQUARE_APPLICATION_ID;
  const LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID;

  useEffect(() => {
    if (!user) { navigate(`/auth?mode=signup&plan=${plan}`); return; }
    api.get("/config").then(({ data }) => setTiers(data.tiers));
  }, [user, plan, navigate]);

  if (!tiers) return <div className="min-h-screen flex items-center justify-center text-ink-dim">LOADING…</div>;
  const tier = tiers[plan];
  if (!tier) return <div className="min-h-screen flex items-center justify-center text-ink-dim">INVALID PLAN</div>;

  const onTokenize = async (token, buyer) => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/subscriptions/subscribe", {
        tier: plan,
        source_id: token.token,
        verification_token: buyer?.token,
      });
      setOk(data);
      await refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : (d?.square_error?.[0]?.detail || "Payment failed"));
    } finally {
      setBusy(false);
    }
  };

  if (ok) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-6">
        <div className="border border-brand-red bg-bg-surface p-10 max-w-lg w-full text-center grain" data-testid="checkout-success">
          <div className="text-[10px] tracking-[0.4em] uppercase text-brand-red">◢ SUBSCRIPTION ACTIVE</div>
          <h1 className="font-display font-black uppercase tracking-tighter text-4xl mt-4">
            YOU&apos;RE <span className="text-brand-red neon-red">IN.</span>
          </h1>
          <p className="text-sm text-ink-mute mt-4">
            Welcome to <span className="text-white font-bold">{tier.name}</span>. Your producer studio is now unlocked.
            Next billing on {new Date(ok.next_billing_at).toLocaleDateString()}.
          </p>
          <Link to="/dashboard" className="mt-8 inline-block bg-brand-red text-black text-xs font-bold tracking-[0.25em] uppercase px-6 py-3 hover:bg-white btn-glitch clip-arrow-r">
            ENTER STUDIO ▸
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-2 gap-10" data-testid="checkout-page">
      <div>
        <div className="text-[10px] tracking-[0.4em] uppercase text-brand-red mb-4">◢ CHECKOUT</div>
        <h1 className="font-display font-black uppercase tracking-tighter text-5xl leading-[0.9]">
          ACTIVATE<br /><span className="text-brand-red">{tier.name}</span>
        </h1>

        <div className="mt-8 border border-zinc-800 bg-bg-surface p-6 grain">
          <div className="flex items-baseline justify-between border-b border-zinc-900 pb-4">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-ink-dim">Plan</div>
              <div className="font-display text-2xl font-black uppercase">{tier.name}</div>
            </div>
            <div className="text-right">
              <div className="font-display text-4xl font-black tracking-tighter">{tier.price_display}</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-ink-dim">/ MONTH</div>
            </div>
          </div>
          <ul className="mt-5 space-y-2">
            {tier.features.slice(0, 6).map((f, i) => (
              <li key={i} className="text-xs text-ink-mute">— {f}</li>
            ))}
          </ul>

          {/* Promo code */}
          <div className="mt-5 pt-5 border-t border-zinc-900">
            <div className="text-[10px] tracking-[0.3em] uppercase text-ink-dim mb-2">PROMO CODE</div>
            {promo ? (
              <div className="flex items-center gap-2 border border-brand-green bg-brand-green/5 px-3 py-2.5" data-testid="checkout-promo-applied">
                <span className="text-brand-green text-[11px] tracking-[0.2em] uppercase font-bold">{promo.code}</span>
                <span className="text-[10px] tracking-[0.25em] uppercase text-white">−{promo.percent_off}%</span>
                <span className="text-[10px] tracking-[0.25em] uppercase text-brand-green ml-auto">SAVED {promo.savings_display}</span>
                <button onClick={removePromo} data-testid="checkout-promo-remove" className="text-ink-mute hover:text-brand-red text-[10px] tracking-[0.3em] uppercase ml-1">✕</button>
              </div>
            ) : (
              <div className="flex border border-zinc-800 focus-within:border-brand-red">
                <input
                  value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                  placeholder="TRAPSZN50"
                  data-testid="checkout-promo-input"
                  className="flex-1 bg-transparent px-3 py-2.5 text-sm font-mono outline-none uppercase"
                />
                <button
                  onClick={applyPromo}
                  disabled={promoBusy || !promoInput}
                  data-testid="checkout-promo-apply"
                  className="bg-brand-red text-black text-[10px] tracking-[0.3em] uppercase font-bold px-4 hover:bg-white disabled:opacity-50"
                >
                  {promoBusy ? "…" : "APPLY"}
                </button>
              </div>
            )}
            {promoErr && <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red mt-2" data-testid="checkout-promo-error">{promoErr}</div>}
            {promo && (
              <div className="mt-3 flex items-baseline justify-between text-sm">
                <span className="text-ink-dim">Total today</span>
                <span className="font-display text-2xl font-black text-brand-green">{promo.final_display}</span>
              </div>
            )}
          </div>
          <div className="mt-5 pt-5 border-t border-zinc-900 flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-ink-dim">
            <ShieldCheck size={14} weight="bold" className="text-brand-green" />
            Square production · PCI compliant · Cancel anytime
          </div>
        </div>

        <div className="mt-6 text-[10px] tracking-[0.3em] uppercase text-ink-dim flex items-center gap-2">
          <Lock size={12} weight="bold" /> CARD DATA TOKENIZED CLIENT-SIDE
        </div>
      </div>

      <div className="border border-zinc-800 bg-bg-surface p-6 grain">
        <div className="flex items-center gap-2 mb-6">
          <Lightning size={16} weight="fill" className="text-brand-red" />
          <span className="text-[10px] tracking-[0.4em] uppercase text-ink-dim">PAYMENT</span>
        </div>

        {APP_ID && LOCATION_ID ? (
          <PaymentForm
            applicationId={APP_ID}
            locationId={LOCATION_ID}
            cardTokenizeResponseReceived={onTokenize}
            createPaymentRequest={() => ({
              countryCode: "US",
              currencyCode: "USD",
              total: { amount: tier.price_cents.toString(), label: `BEAT-LAB ${tier.name}` },
            })}
          >
            <div data-testid="square-card-wrapper" className="mb-4">
              <CreditCard
                buttonProps={{
                  isLoading: busy,
                  css: {
                    backgroundColor: "#FF003C",
                    color: "#000",
                    fontWeight: 700,
                    letterSpacing: "0.25em",
                    textTransform: "uppercase",
                    fontSize: "11px",
                    fontFamily: "JetBrains Mono, monospace",
                    "&:hover": { backgroundColor: "#fff" },
                  },
                }}
              >
                {busy ? "PROCESSING…" : `CHARGE ${tier.price_display} ▸`}
              </CreditCard>
            </div>
          </PaymentForm>
        ) : (
          <div className="text-xs text-brand-red">Missing Square configuration.</div>
        )}

        {err && (
          <div className="mt-4 text-xs text-brand-red border border-brand-red/40 bg-brand-red/10 px-3 py-2" data-testid="checkout-error">
            {err}
          </div>
        )}

        <Link to="/pricing" className="mt-6 block text-center text-[10px] tracking-[0.3em] uppercase text-ink-dim hover:text-white">
          ← BACK TO PLANS
        </Link>
      </div>
    </div>
  );
}
